const SIZE_PREFERENCE_STORAGE_KEY = 'horizon:selected-size';
const STORAGE_TEST_KEY = 'horizon:size-storage-test';
let lastSizeInteraction = null;
const ADD_TO_CART_LABEL = 'Add to Cart';
const OUT_OF_STOCK_LABEL = 'Out of Stock';
const NOTIFY_LABEL = 'Notify Me When Available';
const NOTIFY_LOADING_LABEL = 'Loading...';
const SELECT_SIZE_LABEL = 'Select a size';

const FALLBACK_BUTTON_LABEL = ADD_TO_CART_LABEL;

function getProductMain(root = document) {
  if (root instanceof Element) {
    const productMain = root.closest('main[data-template*="product"]');
    if (productMain) return productMain;
    const nestedProductMain = root.querySelector('main[data-template*="product"]');
    if (nestedProductMain) return nestedProductMain;
  }

  return document.querySelector('main[data-template*="product"]');
}

function getLabelNode(button) {
  const labelWrapper = button?.querySelector('.add-to-cart-text__content');
  if (!(labelWrapper instanceof HTMLElement)) return null;

  const labelNode = labelWrapper.querySelector(':scope > span:first-child');
  if (labelNode instanceof HTMLElement) return labelNode;

  const fallbackLabelNode = document.createElement('span');
  labelWrapper.prepend(fallbackLabelNode);

  return fallbackLabelNode;
}

function cacheNativeLabel(button) {
  const labelNode = getLabelNode(button);
  if (!labelNode) return;

  const currentLabel = labelNode.textContent?.trim() || '';
  if (!currentLabel) return;

  const overriddenLabel = button.dataset.customPdpOverrideLabel || '';
  if (currentLabel !== overriddenLabel) {
    button.dataset.customPdpNativeLabel = currentLabel;
  }
}

function applyButtonState(button, { overrideLabel = null, disabled }) {
  if (!(button instanceof HTMLButtonElement)) return;

  cacheNativeLabel(button);

  const labelNode = getLabelNode(button);
  if (labelNode) {
    if (overrideLabel != null) {
      const safeOverrideLabel = String(overrideLabel || '').trim() || FALLBACK_BUTTON_LABEL;

      labelNode.textContent = safeOverrideLabel;
      button.dataset.customPdpOverrideLabel = safeOverrideLabel;
    } else {
      const nativeLabel = button.dataset.customPdpNativeLabel || labelNode.textContent?.trim() || FALLBACK_BUTTON_LABEL;
      labelNode.textContent = nativeLabel;
      delete button.dataset.customPdpOverrideLabel;
    }
  }

  button.disabled = disabled;
}

function applyNotifyButtonState(button, notifyLink) {
  if (button.dataset.customPdpNativeType == null) {
    button.dataset.customPdpNativeType = button.getAttribute('type') || '';
  }

  if (button.dataset.customPdpNativeOnClick == null) {
    button.dataset.customPdpNativeOnClick = button.getAttribute('on:click') || '';
  }

  const klaviyoReady =
    typeof window.customPdpIsKlaviyoBisReady === 'function' ? window.customPdpIsKlaviyoBisReady() : false;

  applyButtonState(button, {
    overrideLabel: klaviyoReady ? NOTIFY_LABEL : NOTIFY_LOADING_LABEL,
    disabled: !klaviyoReady,
  });

  button.type = 'button';
  button.removeAttribute('on:click');
  button.dataset.customPdpNotifyButton = 'true';

  if (notifyLink instanceof HTMLAnchorElement) {
    button.dataset.customPdpNotifyTarget = notifyLink.dataset.productId || '';
  }

  if (!klaviyoReady && typeof window.customPdpPreloadKlaviyoBis === 'function') {
    window.customPdpPreloadKlaviyoBis();
  }
}

function clearNotifyButtonState(button) {
  if (!(button instanceof HTMLButtonElement)) return;

  delete button.dataset.customPdpNotifyButton;
  delete button.dataset.customPdpNotifyTarget;

  if (button.dataset.customPdpNativeType != null) {
    const nativeType = button.dataset.customPdpNativeType;

    if (nativeType) {
      button.setAttribute('type', nativeType);
    } else {
      button.removeAttribute('type');
    }

    delete button.dataset.customPdpNativeType;
  }

  if (button.dataset.customPdpNativeOnClick != null) {
    const nativeOnClick = button.dataset.customPdpNativeOnClick;

    if (nativeOnClick) {
      button.setAttribute('on:click', nativeOnClick);
    } else {
      button.removeAttribute('on:click');
    }

    delete button.dataset.customPdpNativeOnClick;
  }
}

function syncStickyQuantityDisplay(stickyBar) {
  if (!(stickyBar instanceof HTMLElement)) return;

  const stickyButton = stickyBar.querySelector('[ref="addToCartButton"]');
  const quantityDisplay = stickyBar.querySelector('[ref="quantityDisplay"]');
  const quantityNumber = stickyBar.querySelector('[ref="quantityNumber"]');

  if (!(stickyButton instanceof HTMLButtonElement) || !(quantityDisplay instanceof HTMLElement) || !(quantityNumber instanceof HTMLElement)) {
    return;
  }

  const quantity = Number.parseInt(quantityNumber.textContent || '1', 10);
  quantityDisplay.style.display =
    stickyButton.dataset.customPdpNotifyButton === 'true' || stickyButton.disabled || quantity <= 1 ? 'none' : 'inline';
}

function getSizeControl(variantPicker) {
  if (!(variantPicker instanceof HTMLElement)) return null;

  const explicitFieldset = variantPicker.querySelector('fieldset[data-option-is-size="true"]');
  if (explicitFieldset instanceof HTMLElement) return explicitFieldset;

  const explicitSelect = variantPicker.querySelector('select[data-option-is-size="true"]');
  if (explicitSelect instanceof HTMLElement) return explicitSelect;

  const fallbackFieldsets = Array.from(variantPicker.querySelectorAll('fieldset[data-option-name]'));
  for (const fieldset of fallbackFieldsets) {
    const optionName = fieldset.getAttribute('data-option-name')?.toLowerCase() || '';
    if (optionName.includes('size')) return fieldset;
  }

  const fallbackSelects = Array.from(variantPicker.querySelectorAll('select[data-option-name]'));
  for (const select of fallbackSelects) {
    const optionName = select.getAttribute('data-option-name')?.toLowerCase() || '';
    if (optionName.includes('size')) return select;
  }

  return null;
}

function getWritableLocalStorage() {
  try {
    window.localStorage.setItem(STORAGE_TEST_KEY, '1');
    window.localStorage.removeItem(STORAGE_TEST_KEY);
    return window.localStorage;
  } catch (_error) {
    return null;
  }
}

function normalizeSizeValue(value) {
  const text = String(value || '').trim();
  const match = text.match(/^(?:size\s*)?([0-7])(?:\b|\s|\()/i);

  return match ? match[1] : text.toLowerCase();
}

function readStoredSizePreference() {
  const storage = getWritableLocalStorage();
  if (!storage) return null;

  const rawValue = storage.getItem(SIZE_PREFERENCE_STORAGE_KEY);
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue);
    const normalizedValue = normalizeSizeValue(parsed?.size || parsed?.value || '');

    return normalizedValue ? normalizedValue : null;
  } catch (_error) {
    const normalizedValue = normalizeSizeValue(rawValue);

    return normalizedValue ? normalizedValue : null;
  }
}

function writeStoredSizePreference(value) {
  const normalizedValue = normalizeSizeValue(value);
  if (!normalizedValue) return;

  const storage = getWritableLocalStorage();
  if (!storage) return;

  storage.setItem(
    SIZE_PREFERENCE_STORAGE_KEY,
    JSON.stringify({
      value: String(value || '').trim(),
      size: normalizedValue,
      savedAt: new Date().toISOString(),
    })
  );
  document.dispatchEvent(new Event('horizon:expected-size-change'));
}

function shouldHonorUrlVariant() {
  if (!new URL(window.location.href).searchParams.has('variant')) return false;

  const navigation = performance.getEntriesByType('navigation')?.[0];
  const navigationType = navigation?.type || '';
  if (navigationType === 'reload' || navigationType === 'back_forward') return true;

  if (!document.referrer) return true;

  try {
    return new URL(document.referrer).origin !== window.location.origin;
  } catch (_error) {
    return true;
  }
}

function getOptionValue(option) {
  if (option instanceof HTMLInputElement) {
    return option.value || option.getAttribute('aria-label') || '';
  }

  if (option instanceof HTMLOptionElement) {
    return option.value || option.textContent || '';
  }

  return '';
}

function storeSelectedSize(variantPicker) {
  const sizeControl = getSizeControl(variantPicker);
  if (!(sizeControl instanceof HTMLElement)) return;

  if (sizeControl instanceof HTMLSelectElement) {
    if (sizeControl.value) {
      writeStoredSizePreference(sizeControl.value);
    }
    return;
  }

  const checkedInput = sizeControl.querySelector('input:checked');
  if (checkedInput instanceof HTMLInputElement) {
    writeStoredSizePreference(getOptionValue(checkedInput));
  }
}

function clearSizeSelection(sizeControl) {
  if (sizeControl instanceof HTMLSelectElement) {
    sizeControl.value = '';
    for (const option of sizeControl.options) {
      option.removeAttribute('selected');
    }
    return;
  }

  for (const input of sizeControl.querySelectorAll('input')) {
    if (!(input instanceof HTMLInputElement)) continue;

    input.checked = false;
    input.dataset.currentChecked = 'false';
    input.dataset.previousChecked = 'false';
  }
}

function initializeStoredSizePreference(root = document) {
  const preferredSize = readStoredSizePreference();
  if (!preferredSize && shouldHonorUrlVariant()) {
    delete document.documentElement.dataset.sizePreferencePending;
    return;
  }

  const productMain = getProductMain(root);
  if (!(productMain instanceof HTMLElement)) {
    delete document.documentElement.dataset.sizePreferencePending;
    return;
  }

  const variantPickers = productMain.querySelectorAll('variant-picker[data-template-product-match="true"]');
  for (const variantPicker of variantPickers) {
    if (!(variantPicker instanceof HTMLElement)) continue;

    const sizeControl = getSizeControl(variantPicker);
    if (!(sizeControl instanceof HTMLElement)) continue;

    if (!preferredSize) {
      clearSizeSelection(sizeControl);
      continue;
    }

    if (sizeControl instanceof HTMLSelectElement) {
      const preferredOption = Array.from(sizeControl.options).find(
        (option) => normalizeSizeValue(getOptionValue(option)) === preferredSize && !option.disabled
      );

      if (!preferredOption) {
        clearSizeSelection(sizeControl);
        continue;
      }
      if (sizeControl.value === preferredOption.value) continue;

      sizeControl.value = preferredOption.value;
      sizeControl.dispatchEvent(new Event('change', { bubbles: true }));
      continue;
    }

    const preferredInput = Array.from(sizeControl.querySelectorAll('input')).find(
      (input) => input instanceof HTMLInputElement && !input.disabled && normalizeSizeValue(getOptionValue(input)) === preferredSize
    );

    if (!(preferredInput instanceof HTMLInputElement)) {
      clearSizeSelection(sizeControl);
      continue;
    }
    if (preferredInput.checked) continue;

    preferredInput.click();
  }

  delete document.documentElement.dataset.sizePreferencePending;
}

function getSizeSelectionState(variantPicker) {
  const sizeControl = getSizeControl(variantPicker);
  if (!(sizeControl instanceof HTMLElement)) {
    return { required: false, selected: true };
  }

  if (sizeControl instanceof HTMLSelectElement) {
    return {
      required: true,
      selected: Boolean(sizeControl.value),
    };
  }

  return {
    required: true,
    selected: Boolean(sizeControl.querySelector('input:checked')),
  };
}

function getSelectedOptions(variantPicker) {
  if (!(variantPicker instanceof HTMLElement)) return [];

  const selectedOptions = [];
  const seenNames = new Set();

  const checkedInputs = variantPicker.querySelectorAll('fieldset input:checked');
  for (const input of checkedInputs) {
    if (!(input instanceof HTMLInputElement)) continue;

    const fieldset = input.closest('fieldset');
    const optionName = fieldset?.getAttribute('data-option-name')?.trim() || input.name || '';
    if (!optionName || seenNames.has(optionName)) continue;

    selectedOptions.push({
      name: optionName,
      value: input.value,
    });
    seenNames.add(optionName);
  }

  const selects = variantPicker.querySelectorAll('select[data-option-name]');
  for (const select of selects) {
    if (!(select instanceof HTMLSelectElement)) continue;

    const optionName = select.getAttribute('data-option-name')?.trim() || select.name || '';
    if (!optionName || seenNames.has(optionName) || !select.value) continue;

    selectedOptions.push({
      name: optionName,
      value: select.value,
    });
    seenNames.add(optionName);
  }

  return selectedOptions;
}

function syncStickyVariantTitle(stickyBar, variantPicker) {
  const title = stickyBar?.querySelector('.sticky-add-to-cart__variant');
  if (!(title instanceof HTMLElement)) return;

  const selectedTitle = getSelectedOptions(variantPicker).map((option) => option.value).join(' / ');
  if (selectedTitle) title.textContent = selectedTitle;
}

function getSelectedOptionAvailability(variantPicker) {
  if (!(variantPicker instanceof HTMLElement)) return null;

  let hasAvailability = false;
  const selectedInputs = variantPicker.querySelectorAll('fieldset input:checked');
  for (const input of selectedInputs) {
    if (!(input instanceof HTMLInputElement)) continue;

    if (input.dataset.optionAvailable === 'false') return false;
    if (input.dataset.optionAvailable === 'true') hasAvailability = true;
  }

  const selects = variantPicker.querySelectorAll('select[data-option-name]');
  for (const select of selects) {
    if (!(select instanceof HTMLSelectElement)) continue;

    const selectedOption = select.options[select.selectedIndex];
    if (!(selectedOption instanceof HTMLOptionElement)) continue;

    if (selectedOption.dataset.optionAvailable === 'false') return false;
    if (selectedOption.dataset.optionAvailable === 'true') hasAvailability = true;
  }

  return hasAvailability ? true : null;
}

function getSelectedVariantAvailable(variantPicker, variantId, fallbackDisabled) {
  if (!(variantPicker instanceof HTMLElement)) return !fallbackDisabled;

  const selectedOptionAvailable = getSelectedOptionAvailability(variantPicker);
  if (selectedOptionAvailable != null) return selectedOptionAvailable;

  if (variantPicker.dataset.selectedVariantAvailable === 'true') return true;
  if (variantPicker.dataset.selectedVariantAvailable === 'false') return false;

  const variantJson = variantPicker.querySelector('script[type="application/json"]')?.textContent;
  if (variantJson) {
    try {
      const variant = JSON.parse(variantJson);
      if (!variantId || String(variant?.id || '') === String(variantId)) {
        return Boolean(variant?.available);
      }
    } catch (_error) {
      // Fall back to the button state if Shopify's selected variant JSON is unavailable.
    }
  }

  return !fallbackDisabled;
}

function syncNotifyLink(notifyLink, { variantPicker, variantId, variantAvailable, allVariantsUnavailable, sizeRequired, sizeSelected, willRestock }) {
  if (!(notifyLink instanceof HTMLAnchorElement)) return;

  const selectedOptions = getSelectedOptions(variantPicker);
  const variantTitle = selectedOptions.map((option) => option.value).join(' / ');

  notifyLink.dataset.variantId = variantId || '';
  notifyLink.dataset.variantTitle = variantTitle;
  notifyLink.dataset.variantAvailable = String(variantAvailable);
  notifyLink.dataset.selectedOptions = JSON.stringify(selectedOptions);

  notifyLink.dataset.notifyAvailable = String(
    Boolean(willRestock) && (!(sizeRequired && !sizeSelected) || allVariantsUnavailable) && (allVariantsUnavailable || !variantAvailable)
  );
  notifyLink.hidden = true;
}

function syncProductSection(productForm) {
  if (!(productForm instanceof HTMLElement)) return;

  const productId = productForm.dataset.productId;
  if (!productId) return;

  const section = productForm.closest('.shopify-section');
  if (!(section instanceof HTMLElement)) return;

  const variantPicker = section.querySelector(`variant-picker[data-product-id="${productId}"]`);
  if (!(variantPicker instanceof HTMLElement)) return;

  const stickyBar = section.querySelector(`sticky-add-to-cart[data-product-id="${productId}"]`);
  const mainButton = productForm.querySelector('[ref="addToCartButton"]');
  if (!(mainButton instanceof HTMLButtonElement)) return;

  const stickyButton = stickyBar?.querySelector('[ref="addToCartButton"]');
  const acceleratedCheckout = productForm.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
  const notifyLink = section.querySelector('[data-product-notify-link]');
  const variantIdInput = productForm.querySelector('[ref="variantId"]');

  const variantId = variantIdInput instanceof HTMLInputElement ? variantIdInput.value : '';
  const nativeMainDisabled = mainButton.disabled;
  const nativeStickyDisabled = stickyButton instanceof HTMLButtonElement ? stickyButton.disabled : true;
  const sizeState = getSizeSelectionState(variantPicker);
  const allVariantsUnavailable = notifyLink instanceof HTMLElement ? notifyLink.dataset.allVariantsUnavailable === 'true' : false;
  const willRestock = notifyLink instanceof HTMLElement ? notifyLink.dataset.willRestock === 'true' : false;
  const variantAvailable = getSelectedVariantAvailable(variantPicker, variantId, nativeMainDisabled);
  const notifyButtonActive =
    notifyLink instanceof HTMLAnchorElement &&
    Boolean(willRestock) &&
    (allVariantsUnavailable || !variantAvailable) &&
    (!(sizeState.required && !sizeState.selected) || allVariantsUnavailable);

  syncStickyVariantTitle(stickyBar, variantPicker);

  if (sizeState.required && !sizeState.selected && !notifyButtonActive) {
    clearNotifyButtonState(mainButton);
    applyButtonState(mainButton, {
      overrideLabel: SELECT_SIZE_LABEL,
      disabled: true,
    });

    if (stickyButton instanceof HTMLButtonElement) {
      clearNotifyButtonState(stickyButton);
      applyButtonState(stickyButton, {
        overrideLabel: SELECT_SIZE_LABEL,
        disabled: true,
      });
      syncStickyQuantityDisplay(stickyBar);
    }

    if (acceleratedCheckout instanceof HTMLElement) {
      acceleratedCheckout.hidden = true;
    }
  } else if (notifyButtonActive) {
    applyNotifyButtonState(mainButton, notifyLink);

    if (stickyButton instanceof HTMLButtonElement) {
      applyNotifyButtonState(stickyButton, notifyLink);
      syncStickyQuantityDisplay(stickyBar);
    }

    if (acceleratedCheckout instanceof HTMLElement) {
      acceleratedCheckout.hidden = true;
    }
  } else {
    clearNotifyButtonState(mainButton);
    applyButtonState(mainButton, {
      overrideLabel: variantAvailable ? ADD_TO_CART_LABEL : OUT_OF_STOCK_LABEL,
      disabled: variantAvailable ? nativeMainDisabled : true,
    });

    if (stickyButton instanceof HTMLButtonElement) {
      clearNotifyButtonState(stickyButton);
      applyButtonState(stickyButton, {
        overrideLabel: variantAvailable ? ADD_TO_CART_LABEL : OUT_OF_STOCK_LABEL,
        disabled: variantAvailable ? nativeStickyDisabled : true,
      });
      syncStickyQuantityDisplay(stickyBar);
    }

    if (acceleratedCheckout instanceof HTMLElement) {
      acceleratedCheckout.hidden = !variantAvailable || nativeMainDisabled;
    }
  }

  syncNotifyLink(notifyLink, {
    variantPicker,
    variantId,
    variantAvailable,
    allVariantsUnavailable,
    sizeRequired: sizeState.required,
    sizeSelected: sizeState.selected,
    willRestock,
  });
}

function syncPurchaseState(root = document) {
  const productMain = getProductMain(root);
  if (!(productMain instanceof HTMLElement)) return;

  const productForms = productMain.querySelectorAll('product-form-component[data-product-id]');
  for (const productForm of productForms) {
    syncProductSection(productForm);
  }
}

function scheduleSync(root = document) {
  requestAnimationFrame(() => {
    initializeStoredSizePreference(root);
    syncPurchaseState(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleSync());
} else {
  scheduleSync();
}

window.addEventListener('pageshow', () => scheduleSync());
window.addEventListener('storage', (event) => {
  if (event.key === SIZE_PREFERENCE_STORAGE_KEY) scheduleSync();
});
window.addEventListener('custom-pdp:klaviyo-bis-ready', () => scheduleSync());
document.addEventListener('shopify:section:load', (event) => {
  scheduleSync(event.target instanceof Element ? event.target : document);
});
document.addEventListener('variant:update', (event) => {
  const target = event.target;
  const variantPicker = target instanceof Element ? target.closest('variant-picker') : null;
  const available = event.detail?.resource?.available;

  if (variantPicker instanceof HTMLElement && typeof available === 'boolean') {
    variantPicker.dataset.selectedVariantAvailable = String(available);
  }

  scheduleSync(event.target instanceof Element ? event.target : document);
});
function recordSizeInteraction(event) {
  if (!event.isTrusted || !(event.target instanceof HTMLElement)) return;

  const variantPicker = event.target.closest('variant-picker');
  const sizeControl = getSizeControl(variantPicker);
  if (!(sizeControl instanceof HTMLElement) || !sizeControl.contains(event.target)) return;

  lastSizeInteraction = { sizeControl, timestamp: performance.now() };
}

document.addEventListener('click', recordSizeInteraction, true);
document.addEventListener('pointerdown', (event) => {
  if (event.target instanceof HTMLSelectElement) recordSizeInteraction(event);
}, true);
document.addEventListener('keydown', (event) => {
  if ([' ', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
    recordSizeInteraction(event);
  }
}, true);
document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const variantPicker = target.closest('variant-picker');
  if (!(variantPicker instanceof HTMLElement)) return;

  const sizeControl = getSizeControl(variantPicker);
  if (!(sizeControl instanceof HTMLElement) || !sizeControl.contains(target)) return;

  const userInitiated = event.isTrusted &&
    lastSizeInteraction?.sizeControl === sizeControl &&
    performance.now() - lastSizeInteraction.timestamp < 30000;
  lastSizeInteraction = null;
  if (!userInitiated) return;

  storeSelectedSize(variantPicker);
});
document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-custom-pdp-notify-button="true"]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const section = button.closest('.shopify-section');
    const notifyLink = section?.querySelector('[data-product-notify-link]');
    if (!(notifyLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    event.stopPropagation();

    if (typeof window.customPdpOpenKlaviyoBis === 'function') {
      window.customPdpOpenKlaviyoBis(notifyLink);
      return;
    }

    notifyLink.click();
  },
  true
);

for (const eventName of ['pointerenter', 'focusin', 'touchstart']) {
  document.addEventListener(
    eventName,
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-custom-pdp-notify-button="true"]') : null;
      if (!(button instanceof HTMLButtonElement)) return;

      if (typeof window.customPdpPreloadKlaviyoBis === 'function') {
        window.customPdpPreloadKlaviyoBis({ immediate: true });
      }
    },
    true
  );
}
